import { type FocusEvent, type FormEvent, type ReactNode, useCallback, useId, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { ArrowRight, ChevronDown, ChevronUp, Sparkles } from 'lucide-react'
import { formatMoney } from '@/lib/utils'
import {
  citedRefs,
  overviewBlocks,
  overviewParts,
  useAiOverview,
  type AiOverviewRequest,
  type AiOverviewState,
} from './ai-overview-api'
import type { ShopifySearchProduct } from './storefront-api'
import type { SearchOverviewChat } from './storefront-chat-api'
import { normalizeProductTitle } from './storefront-format'

const MAX_SOURCES = { autocomplete: 2, results: 4 } as const
const MAX_ALTERNATIVES = 3

function productPrice(product: ShopifySearchProduct) {
  return formatMoney(product.quantity_pricing?.quantityOnePrice ?? product.price)
}

function shippingLabel(product: ShopifySearchProduct) {
  if (product.free_shipping) return 'Free Shipping'
  return product.small_parcel_free_freight_eligible ? 'Free shipping eligible' : null
}

export function AiOverview({
  request,
  state,
  answer = false,
  onSearch,
  onFollowUp,
}: {
  request: AiOverviewRequest | null
  /** The overview already streamed by the page for `request`; omitted, this streams its own. */
  state?: AiOverviewState | null
  /** No words matched: the page shows the named products as full cards, so no source list here. */
  answer?: boolean
  /** In the search box's suggestions a product searches its model, like a product suggestion. */
  onSearch?: (query: string) => void
  /** "Ask a follow-up" opens the store chat with this search and answer (USA-1257). */
  onFollowUp?: (chat: SearchOverviewChat, question: string | null) => void
}) {
  const streamed = useAiOverview(state === undefined ? request : null)
  const overview = state === undefined ? streamed : state
  // Results: the answer sits in a fixed-height fold while it loads and writes, so nothing below
  // moves; a finished answer that fits shrinks to fit, a longer one opens with Show more (USA-1248).
  const [overflowing, setOverflowing] = useState(false)
  const [expandedFor, setExpandedFor] = useState<AiOverviewRequest | null>(null)
  const expanded = expandedFor !== null && expandedFor === request
  const [question, setQuestion] = useState('')
  const askId = useId()
  const measureFold = useCallback((fold: HTMLDivElement | null) => {
    const content = fold?.firstElementChild
    if (!fold || !content) return
    const observer = new ResizeObserver(() => {
      if (fold.hasAttribute('data-expanded')) return
      // Against the fold's limit, not its height: a fitted answer is exactly as tall as its fold.
      const limit = parseFloat(getComputedStyle(fold).getPropertyValue('--sf-ai-fold')) || fold.clientHeight
      setOverflowing(content.scrollHeight > limit + 1)
    })
    observer.observe(fold)
    observer.observe(content)
    return () => observer.disconnect()
  }, [])
  if (!request || !overview) return null
  const compact = request.placement === 'autocomplete'
  const working = overview.phase === 'loading' || overview.phase === 'streaming'
  if (compact && overview.phase === 'error') return null
  if (overview.phase === 'done' && !overview.text.trim()) return null

  const { sources } = overview
  // An exact model search shows that product as one block with alternatives in a row (USA-1250).
  const match = compact || overview.exact === null ? undefined : sources[overview.exact]
  const cited = citedRefs(overview.text, sources)
    .filter((ref) => !match || ref !== overview.exact)
    .slice(0, match ? MAX_ALTERNATIVES : MAX_SOURCES[request.placement])

  const productTarget = (
    product: ShopifySearchProduct,
    className: string,
    label: string | undefined,
    children: ReactNode,
  ) =>
    onSearch ? (
      <button
        type="button"
        className={className}
        aria-label={label}
        onPointerDown={(event) => event.preventDefault()}
        onClick={() => onSearch(product.model)}
      >
        {children}
      </button>
    ) : (
      <Link to="/products/$productId" params={{ productId: product.uuid }} className={className} aria-label={label}>
        {children}
      </Link>
    )

  const inline = (text: string) =>
    overviewParts(text).map((part, index) => {
      if (typeof part === 'string') return part
      const product = sources[part.ref]
      if (!part.name) return null
      if (!product) return part.name
      if (!onSearch) return <span key={index}>{productTarget(product, 'sf-ai__product', undefined, part.name)}</span>
      // A link, not a button, so a long name wraps inside the sentence.
      return (
        <Link
          key={index}
          to="/catalog-search"
          search={{ q: product.model }}
          className="sf-ai__product"
          onPointerDown={(event) => event.preventDefault()}
          onClick={(event) => {
            event.preventDefault()
            onSearch(product.model)
          }}
        >
          {part.name}
        </Link>
      )
    })

  const blocks = overviewBlocks(overview.text, overview.phase === 'streaming')
  const Heading = compact ? 'p' : 'h2'
  const foldable = !compact && overview.phase !== 'error'
  const clipped = foldable && overflowing && !expanded
  // Tabbing to a link hidden under the fold opens the answer instead of focusing it out of sight.
  const revealFocus = (event: FocusEvent<HTMLDivElement>) => {
    if (!clipped) return
    const fold = event.currentTarget.getBoundingClientRect()
    if (event.target.getBoundingClientRect().bottom > fold.bottom) setExpandedFor(request)
  }

  const body = (
    <div className="sf-ai__body">
      {blocks.map((block, index) =>
        block.kind === 'p' ? (
          <p key={index}>{inline(block.text)}</p>
        ) : (
          <ul key={index}>
            {block.items.map((item, itemIndex) => (
              <li key={itemIndex}>{inline(item)}</li>
            ))}
          </ul>
        ),
      )}
    </div>
  )

  const matchBlock = (product: ShopifySearchProduct) => {
    const title = normalizeProductTitle(product.title)
    const shipping = shippingLabel(product)
    return (
      <div className="sf-ai__match">
        <Link
          to="/products/$productId"
          params={{ productId: product.uuid }}
          className="sf-ai__match-image"
          tabIndex={-1}
          aria-hidden="true"
        >
          {product.thumbnail ? (
            <img src={product.thumbnail} alt="" width={88} height={88} />
          ) : (
            <span className="sf-ai__source-blank" />
          )}
        </Link>
        <Link
          to="/products/$productId"
          params={{ productId: product.uuid }}
          className="sf-ai__match-title"
        >
          {title}
        </Link>
        <div className="sf-ai__match-price">
          <strong>{productPrice(product)}</strong>
          {shipping ? <span>{shipping}</span> : null}
        </div>
        <div className="sf-ai__match-facts">
          {overview.phase === 'error' ? (
            <p className="sf-ai__status">The AI overview is unavailable right now.</p>
          ) : overview.phase === 'loading' ? (
            <span className="sf-skeleton" aria-hidden="true" />
          ) : (
            body
          )}
        </div>
      </div>
    )
  }

  const chat = overview.chat
  const ask = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!chat || !onFollowUp) return
    onFollowUp(chat, question.trim() || null)
    setQuestion('')
  }
  // The search box has no room for a field: a link in the heading row opens the chat instead.
  const askLink =
    compact && onFollowUp && chat ? (
      <button
        type="button"
        className="sf-ai__ask-link"
        onPointerDown={(event) => event.preventDefault()}
        onClick={() => onFollowUp(chat, null)}
      >
        Ask a follow-up
        <ArrowRight size={14} strokeWidth={2} aria-hidden="true" />
      </button>
    ) : null

  return (
    <section
      className={`sf-ai ${compact ? 'sf-ai--compact' : 'sf-ai--results'} ${match ? 'sf-ai--match' : ''}`}
      aria-label="AI overview"
      aria-busy={working}
      data-phase={overview.phase}
      data-e2e={`ai-overview-${request.placement}`}
    >
      {askLink ? (
        <div className="sf-ai__top">
          <AiOverviewHeading as={Heading} />
          {askLink}
        </div>
      ) : (
        <AiOverviewHeading as={Heading} />
      )}

      <div
        ref={measureFold}
        className="sf-ai__fold"
        data-expanded={(foldable && expanded) || undefined}
        data-clipped={clipped || undefined}
        data-fit={(foldable && overview.phase === 'done' && !overflowing) || undefined}
        onFocus={revealFocus}
      >
        <div className="sf-ai__content">
          {match ? (
            matchBlock(match)
          ) : overview.phase === 'error' ? (
            <p className="sf-ai__status">The AI overview is unavailable right now.</p>
          ) : overview.phase === 'loading' ? (
            <OverviewLoading status={overview.status} compact={compact} />
          ) : (
            body
          )}

          {cited.length && overview.phase === 'done' && !answer ? (
            <ul
              className="sf-ai__sources"
              aria-label={match ? 'Alternatives' : 'Products in this overview'}
            >
              {cited.map((ref) => {
                const product = sources[ref]
                if (!product) return null
                const title = normalizeProductTitle(product.title)
                return (
                  <li key={ref}>
                    {productTarget(
                      product,
                      'sf-ai__source',
                      `${title}, ${productPrice(product)}`,
                      <>
                        {product.thumbnail ? (
                          <img src={product.thumbnail} alt="" width={48} height={48} loading="lazy" />
                        ) : (
                          <span className="sf-ai__source-blank" aria-hidden="true" />
                        )}
                        <span className="sf-ai__source-copy">
                          <span className="sf-ai__source-vendor">{product.vendor.title}</span>
                          <span className="sf-ai__source-title">{match ? product.model : title}</span>
                          <span className="sf-ai__source-price">{productPrice(product)}</span>
                        </span>
                      </>,
                    )}
                  </li>
                )
              })}
            </ul>
          ) : null}
        </div>
        {foldable && overview.phase === 'done' && overflowing ? (
          <button
            type="button"
            className="sf-ai__more"
            aria-expanded={expanded}
            onClick={() => setExpandedFor(expanded ? null : request)}
          >
            {expanded ? 'Show less' : 'Show more'}
            {expanded ? (
              <ChevronUp size={16} strokeWidth={2} aria-hidden="true" />
            ) : (
              <ChevronDown size={16} strokeWidth={2} aria-hidden="true" />
            )}
          </button>
        ) : null}
      </div>

      {/* Held from loading to done so nothing moves; usable once the answer is ready. */}
      {!compact && onFollowUp && overview.phase !== 'error' && (working || chat) ? (
        <form className="sf-ai__ask" onSubmit={ask} data-e2e="ai-overview-follow-up">
          <label className="sr-only" htmlFor={askId}>
            Ask a follow-up
          </label>
          <input
            id={askId}
            className="sf-ai__ask-input"
            type="text"
            value={question}
            maxLength={2000}
            placeholder="Ask a follow-up"
            autoComplete="off"
            disabled={!chat}
            onChange={(event) => setQuestion(event.target.value)}
          />
          <button type="submit" className="sf-ai__ask-send" aria-label="Ask in chat" disabled={!chat}>
            <ArrowRight size={18} strokeWidth={2} aria-hidden="true" />
          </button>
        </form>
      ) : null}

      <span className="sr-only" role="status" aria-live="polite">
        {working ? 'Writing AI overview' : overview.phase === 'done' ? 'AI overview ready' : ''}
      </span>
    </section>
  )
}

function AiOverviewHeading({ as: Heading }: { as: 'p' | 'h2' }) {
  return (
    <Heading className="sf-ai__heading">
      <Sparkles size={16} strokeWidth={1.75} aria-hidden="true" />
      AI overview
    </Heading>
  )
}

function OverviewLoading({ status, compact }: { status: string; compact: boolean }) {
  return (
    <div className="sf-ai__pending">
      <p className="sf-ai__status">{status || 'Searching the catalog'}…</p>
      <span className="sf-skeleton" aria-hidden="true" />
      {compact ? null : (
        <>
          <span className="sf-skeleton" aria-hidden="true" />
          <span className="sf-skeleton" aria-hidden="true" />
          <span className="sf-skeleton sf-skeleton--short" aria-hidden="true" />
        </>
      )}
    </div>
  )
}

/** The search box's overview while the shopper is still typing: its slot, already in place. */
export function AiOverviewPending() {
  return (
    <section className="sf-ai sf-ai--compact" aria-label="AI overview" aria-busy data-phase="loading">
      <AiOverviewHeading as="p" />
      <OverviewLoading status="" compact />
    </section>
  )
}
